import { useQueryClient } from "@tanstack/react-query";
import {
  CreditCard,
  Coins,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  ShieldCheck,
  RefreshCw,
  Loader2,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { usePaymentsOverview, useRecentPayments } from "@/hooks/use-queries";
import { formatDistanceToNow } from "date-fns";

const usd = (n: number | null | undefined) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function Stat({
  label,
  value,
  sub,
  icon: Icon,
  warn,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: any;
  warn?: boolean;
}) {
  return (
    <Card className={`glass-panel ${warn ? "border-amber-500/40 bg-amber-500/5" : ""}`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
          <Icon className={`h-4 w-4 ${warn ? "text-amber-400" : "text-primary"}`} />
        </div>
        <div className="text-2xl font-display font-bold">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

const STATUS_STYLES: Record<string, string> = {
  CAPTURED: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  AUTHORIZED: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  PENDING_AUTH: "bg-muted text-muted-foreground border-border",
  VOIDED: "bg-muted text-muted-foreground border-border",
  REFUNDED: "bg-violet-500/15 text-violet-400 border-violet-500/30",
  FAILED: "bg-red-500/15 text-red-400 border-red-500/30",
};

export default function AdminPayments() {
  const { data, isLoading, isFetching } = usePaymentsOverview();
  const { data: recent } = useRecentPayments(100);
  const queryClient = useQueryClient();
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-payments-overview"] });
    queryClient.invalidateQueries({ queryKey: ["admin-payments-recent"] });
  };

  const float = data?.float;
  const floatUnhealthy = float?.healthy === false;

  return (
    <div className="space-y-8 pb-10">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="text-sm font-mono font-medium text-primary uppercase tracking-[0.2em] mb-1">Finance & Ops</h2>
          <h1 className="text-3xl font-display font-bold">Payments</h1>
          <p className="text-muted-foreground mt-1">Card-in / USDC-out activity, revenue, and treasury float. Auto-refreshes every 30s.</p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} className="border-divider">
          {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {/* Treasury float health — the operational gauge */}
      {float && (
        <Card className={`glass-panel ${floatUnhealthy ? "border-amber-500/50 bg-amber-500/5" : "border-emerald-500/30 bg-emerald-500/[0.04]"}`}>
          <CardContent className="p-5 flex items-start gap-4">
            {floatUnhealthy ? (
              <AlertTriangle className="h-6 w-6 text-amber-400 shrink-0 mt-0.5" />
            ) : (
              <ShieldCheck className="h-6 w-6 text-emerald-400 shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <div className="font-semibold mb-1">
                {floatUnhealthy ? "Treasury float can't cover pending card payouts" : "Treasury float is healthy"}
              </div>
              <p className="text-sm text-muted-foreground">
                Treasury holds <span className="text-foreground font-mono">{usd(float.treasuryUsdc)}</span> USDC;{" "}
                <span className="text-foreground font-mono">{usd(float.owedUsdc)}</span> is owed to authorized card jobs awaiting approval.
                {floatUnhealthy && " Fund the treasury with USDC before approving more card jobs."}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stat grid */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => <Skeleton key={i} className="h-28 rounded-xl bg-surface-subtle" />)}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Card jobs" value={String(data?.cardJobs ?? 0)} icon={CreditCard} sub={`${data?.usdcJobs ?? 0} USDC-funded`} />
          <Stat label="Card GMV (captured)" value={usd(data?.cardGmvUsdc)} icon={TrendingUp} sub={`fees ${usd(data?.cardFeesUsdc)}`} />
          <Stat label="Authorized (held)" value={usd(data?.authorizedVolumeUsdc)} icon={CreditCard} sub="not yet captured" />
          <Stat label="Refunds" value={String(data?.refundedCount ?? 0)} icon={RefreshCw} />
          <Stat label="Stripe available" value={usd(data?.stripeBalance?.availableUsd)} icon={DollarSign} sub={data?.stripeBalance ? (data.stripeBalance.livemode ? "live" : "test") : "n/a"} />
          <Stat label="Stripe pending" value={usd(data?.stripeBalance?.pendingUsd)} icon={DollarSign} />
          <Stat label="Treasury USDC" value={usd(float?.treasuryUsdc)} icon={Coins} warn={floatUnhealthy} />
          <Stat label="Owed to card jobs" value={usd(float?.owedUsdc)} icon={Coins} warn={floatUnhealthy} />
        </div>
      )}

      {/* Status breakdown */}
      {data?.byStatus && Object.keys(data.byStatus).length > 0 && (
        <Card className="glass-panel">
          <CardContent className="p-5">
            <h3 className="text-sm font-mono uppercase tracking-wider text-muted-foreground mb-3">Card payment status</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(data.byStatus as Record<string, { count: number; usdc: number }>).map(([st, v]) => (
                <Badge key={st} variant="outline" className={`${STATUS_STYLES[st] || "border-border"} font-mono`}>
                  {st}: {v.count} · {usd(v.usdc)}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent card transactions */}
      <Card className="glass-panel">
        <CardContent className="p-5">
          <h3 className="text-sm font-mono uppercase tracking-wider text-muted-foreground mb-4">Recent card transactions</h3>
          {!recent || recent.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No card payments yet.</p>
          ) : (
            <div className="divide-y divide-divider">
              {recent.map((p: any) => (
                <div key={p.id} className="flex items-center gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{p.title || "Untitled job"}</p>
                    <p className="text-xs text-muted-foreground font-mono">
                      {p.poster} · {p.updatedAt ? formatDistanceToNow(new Date(p.updatedAt), { addSuffix: true }) : ""}
                    </p>
                  </div>
                  <span className="font-mono text-sm">{usd(p.amountUsdc)}</span>
                  <Badge variant="outline" className={`${STATUS_STYLES[p.paymentStatus] || "border-border"} font-mono text-[10px] shrink-0`}>
                    {p.paymentStatus || "—"}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
